<template>
  <div>
    <h1>Lista de Oportunidades</h1>
    <b-card>
      <b-row class="mb-2" align-v="center">
        <div class="col-8">
          <b-input-group size="sm">
            <b-form-input
              v-model="termo_pesquisa"
              v-on:keyup.enter="searchTerm"
              placeholder="Pesquisar"
            ></b-form-input>
            <b-input-group-append>
              <b-button variant="outline-exitos" v-b-modal.modal-busca-avancada>
                <font-awesome-icon icon="filter" />
                Busca avançada
              </b-button>
              <b-button @click="searchTerm" variant="exitos">
                <font-awesome-icon icon="magnifying-glass" />
                Buscar
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div class="ml-auto">
          <b-button
            variant="exitos"
            :to="{ name: 'oportunidades_create' }"
            v-if="$can('oportunidade:create')"
          >
            <font-awesome-icon icon="plus" />
            Nova Oportunidade
          </b-button>
        </div>
      </b-row>

      <b-row class="mb-2" align-v="center">
        <div class="col-12">
          <FiltersList :filters="showFilters" />
        </div>
      </b-row>
      <b-table
        striped
        hover
        :items="oportunidades_list"
        :fields="fields"
        :busy="isBusy"
        @sort-changed="sortingChanged"
        show-empty
        sort-icon-left
        class="list-oportunidades"
      >
        <template #cell(id)="{ item }">
          <div class="d-flex flex-column">
            <h5>#{{ item.id }}</h5>
            <span class="text-muted">{{ item.codigo }}</span>
          </div>
        </template>

        <template #cell(nome)="{ item }">
          <div class="d-flex flex-column">
            <router-link
              :to="{ name: 'oportunidades_edit', params: { id: item.id } }"
            >
              <h5>{{ item.nome }}</h5>
            </router-link>

            <span class="text-muted">{{ item.concedente_nome }}</span>
          </div>
        </template>

        <template #cell(tipos_concedentes.nome)="{ item }">
          <div>
            {{ item.origem }}
          </div>
          <hr class="my-2" />
          <div class="d-flex flex-column">
            <div
              class="d-inline-block text-truncate text-muted"
              style="max-width: 150px"
            >
              {{ item.categorias_nomes }}
            </div>
          </div>
        </template>

        <template #cell(rec_propostas)="{ item }">
          <div
            class="cell-rec_propostas"
            :class="{
              'cell-rec_propostas-sim': isDatePast(item.data_proposta_fim),
            }"
          >
            {{ item.data_proposta_fim | date_past }}
          </div>
        </template>

        <template #cell(valores)="{ item }">
          <div>
            <small class="d-block text-muted">Máx por projeto</small>
            <small class="text-uppercase">
              {{ item.moeda }}
            </small>
            {{ item.valor_projeto | number_format }}
          </div>
          <hr class="my-2" />
          <div>
            <small class="d-block text-muted">Total por Oportunidade</small>
            <small class="text-uppercase">
              {{ item.moeda }}
            </small>
            {{ item.valor_total | number_format }}
          </div>
        </template>

        <template #cell(data_proposta_fim)="{ item }">
          <div class="d-flex flex-column">
            <div v-show="item.data_proposta_fim">
              {{ item.data_proposta_fim | format_date }}
            </div>
            <div>{{ item.data_proposta_fim_tipo | tipo_proposta }}</div>
          </div>
        </template>

        <template #cell(updated_at)="{ item }">
          <div class="d-flex flex-column">
            <div>{{ item.updated_at | date_huminized }}</div>
            <small>{{ item.updated_at | format_datetime }}</small>
          </div>
        </template>

        <template #cell(actions)="row">
          <Actions
            :item="row.item"
            :view_show="false"
            :view_edit="$can('oportunidade:edit')"
            :view_delete="$can('oportunidade:delete')"
            edit_to="oportunidades_edit"
            @delete="deleteItem"
          />
        </template>

        <template #empty="{}">
          <h5 class="text-center">Nenhum item encontado</h5>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </b-card>

    <div>
      <pagination-nav :pagination="pagination" @input="loadData()" />
    </div>
    <b-modal
      id="modal-busca-avancada"
      ref="modal-busca-avancada"
      title="Busca Avançada"
      body-class="py-0"
      size="xl"
      scrollable
      ok-variant="exitos"
      @ok="activeFilters"
    >
      <busca-avancada @input="form = $event" ref="busca-avancada" />
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import moment from "moment";
import mixins from "@/components/Table/mixins";
import BuscaAvancada from "@/modules/oportunidades/Components/BuscaAvancada.vue";
import FiltersList from "@/modules/oportunidades/Components/FiltersList.vue";
import { isEqual } from "lodash";
import {
  convertQuery,
  convertQueryToApi,
  convertFormToApi,
} from "@/helpers/functions/oportunidade_query";

export default {
  name: "ListOportunidades",
  components: {
    BuscaAvancada,
    FiltersList,
  },
  mixins: [mixins],
  data: () => ({
    showFilters: {},
  }),
  computed: {
    ...mapState("oportunidades", [
      "oportunidades_list",
      "oportunidades",
      "pagination",
    ]),
    ...mapGetters("oportunidades", ["qualificacao_proposta"]),
    fields() {
      return [
        { key: "id", label: "COD", class: "col-1", sortable: true },
        { key: "nome", label: "nome", class: "text-wrap", sortable: true },
        {
          key: "tipos_concedentes.nome",
          label: "Origem / Categoria",
          class: "col-2",
          sortable: true,
          sortKey: "tipos_concedentes.nome",
        },
        { key: "valores", label: "Valores", class: "col-2" },
        { key: "rec_propostas", label: "Recebendo Propostas", class: "col-1" },
        {
          key: "data_proposta_fim",
          label: "Prazo",
          class: "col-1",
          sortable: true,
        },
        {
          key: "updated_at",
          label: "Última atualização",
          class: "col-1",
          sortable: true,
        },
        { key: "actions", label: "", class: "cell-actions" },
      ];
    },
  },
  methods: {
    ...mapActions("oportunidades", [
      "ActionGetAll",
      "ActionList",
      "ActionDelete",
    ]),
    ...mapActions("moedas", { getAllMoedas: "ActionToSelect" }),
    isDatePast(value) {
      if (!value) {
        return true;
      }
      const isPast = moment(value, ["YYYY-MM-DD"]).isAfter(moment(), "days");
      return isPast;
    },
    getMoedaById(id) {
      const selected = find(this.moedas, { value: id });
      return selected ? selected.simbolo : "R$";
    },
    async activeFilters() {
      const form = this.$refs["busca-avancada"].form;

      await this.$router.push({ query: convertFormToApi(form) });
      this.loadData();
    },
    async loadData() {
      const query = this.$route.query;
      const form = await convertQueryToApi(query);
      this.showFilters = await convertQuery(query);

      this.isBusy = true;
      try {
        let payload = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          orderBy: this.sortBy,
          sortBy: this.sortDesc ? "desc" : "asc",
          q: this.termo_pesquisa,
          ...form,
        };

        if (!isEqual(this._last_request, payload)) {
          this._last_request = payload;
          await this.ActionList(payload);
        }
      } catch (error) {
        this.$notifications.addNotification({
          message: "Erro ao carregar dados da listagem",
          icon: "exclamation-triangle",
          type: "danger",
        });
      }
      this.isBusy = false;
    },
  },
  filters: {
    date_huminized: function (value) {
      if (!value) {
        return "Indeterminado";
      }
      return moment(value).fromNow();
    },
    date_past: function (value) {
      if (!value) {
        return "Sim";
      }
      const diff_days = moment(value, ["YYYY-MM-DD"]).isAfter(moment(), "days");
      return diff_days ? "Sim" : "Não";
    },
    tipo_proposta: (value) => {
      const tipos = {
        determinada: "Determinada",
        indeterminada: "Indeterminada",
        continua: "Contínua",
      };
      return tipos[value] ? tipos[value] : "Indeterminada";
    },
  },
  async created() {},
};
</script>

<style lang="scss">
.list-oportunidades {
  tr {
    th {
      white-space: normal !important;
    }
  }

  .cell-rec_propostas {
    font-weight: bold;
    border: 1px solid #ddd;
    padding: 0.3rem;
    text-align: center;
    color: #f5365c;
    text-transform: uppercase;

    &.cell-rec_propostas-sim {
      color: #2dce89;
    }
  }
}
</style>
